import { useEffect, useState, useMemo } from 'react';
import { trackRender } from '../utils/debugRender';
import { errorsApi } from '../services/api';
import toast from 'react-hot-toast';
import { Alert, Badge, Button, Select, TextInput, Card, Pagination } from 'flowbite-react';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

export default function Errors() {
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({ source: '', userId: '', page: 1 });

  // New filter states
  const [severityFilter, setSeverityFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all'); // all, today, week, month
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedError, setSelectedError] = useState(null);

  // 🔍 DEBUG: Track renders to identify infinite loops
  trackRender('Errors', { loading, filters, severityFilter, dateRange, searchQuery });

  useEffect(() => {
    loadErrors();
  }, [filters.source, filters.userId, filters.page]);

  async function loadErrors() {
    try {
      setLoading(true);
      const data = await errorsApi.list(filters);
      setErrors(data.errors || []);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to load errors:', error);
      toast.error('Failed to load errors');
    } finally {
      setLoading(false);
    }
  }

  function handleFilterChange(key, value) {
    setFilters({ ...filters, [key]: value, page: 1 });
  }

  // Client-side filtering for additional filters
  const filteredErrors = useMemo(() => {
    if (!errors) return [];

    return errors.filter((error) => {
      // Severity filter
      if (severityFilter !== 'all' && error.severity?.toLowerCase() !== severityFilter) {
        return false;
      }

      // Date range filter
      if (dateRange !== 'all') {
        const errorDate = new Date(error.created_at);
        const now = new Date();
        const dayInMs = 24 * 60 * 60 * 1000;

        if (dateRange === 'today' && now - errorDate > dayInMs) return false;
        if (dateRange === 'week' && now - errorDate > 7 * dayInMs) return false;
        if (dateRange === 'month' && now - errorDate > 30 * dayInMs) return false;
      }

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          error.error_message?.toLowerCase().includes(query) ||
          error.user_id?.toLowerCase().includes(query) ||
          error.stack_trace?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [errors, severityFilter, dateRange, searchQuery]);

  // Calculate statistics
  const stats = useMemo(() => {
    const last24h = errors.filter(
      (e) => new Date(e.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
    ).length;

    return {
      total: pagination?.total || 0,
      critical: errors.filter((e) => e.severity?.toLowerCase() === 'critical').length,
      errors: errors.filter((e) => e.severity?.toLowerCase() === 'error').length,
      warnings: errors.filter((e) => e.severity?.toLowerCase() === 'warning').length,
      frontend: errors.filter((e) => e.source === 'frontend').length,
      worker: errors.filter((e) => e.source === 'worker').length,
      last24h,
    };
  }, [errors, pagination]);

  // Mark error as resolved (placeholder)
  async function handleMarkResolved(errorId) {
    try {
      toast.success('Error marked as resolved');
      // TODO: Implement API call when backend supports it
    } catch (error) {
      console.error('Failed to mark error as resolved:', error);
      toast.error('Failed to mark error as resolved');
    }
  }

  // Calculate total pages
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.limit) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Error Tracking</h1>
          <div className="mt-2 flex items-center gap-3">
            <Badge color="gray" size="sm">
              {stats.total} total errors
            </Badge>
            <Badge color="info" size="sm">
              {filteredErrors.length} filtered
            </Badge>
            <Badge color="failure" size="sm">
              {stats.last24h} last 24h
            </Badge>
          </div>
        </div>

        <Button color="light" size="sm" onClick={loadErrors} disabled={loading}>
          <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Summary - Flowbite Alerts */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Alert key="critical-stat" color="failure">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium">Critical</span>
                <p className="mt-1 text-2xl font-bold">{stats.critical}</p>
              </div>
            </div>
          </div>
        </Alert>

        <Alert key="error-stat" color="warning">
          <div className="flex items-start gap-2">
            <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium">Errors</span>
                <p className="mt-1 text-2xl font-bold">{stats.errors}</p>
              </div>
            </div>
          </div>
        </Alert>

        <Alert key="warning-stat" color="info">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium">Warnings</span>
                <p className="mt-1 text-2xl font-bold">{stats.warnings}</p>
              </div>
            </div>
          </div>
        </Alert>

        <Alert key="source-stat" color="gray">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium">Sources</span>
              <p className="mt-1 text-sm">
                <Badge color="info" size="xs" className="mr-1">
                  Frontend: {stats.frontend}
                </Badge>
                <Badge color="purple" size="xs">
                  Worker: {stats.worker}
                </Badge>
              </p>
            </div>
          </div>
        </Alert>
      </div>

      {/* Filter Sidebar */}
      <Card>
        <div className="grid gap-4 md:grid-cols-12">
          {/* Search Input */}
          <div className="md:col-span-3">
            <TextInput
              icon={FunnelIcon}
              placeholder="Search errors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sizing="md"
            />
          </div>

          {/* Source Filter */}
          <div className="md:col-span-2">
            <Select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              sizing="md"
            >
              <option value="">All Sources</option>
              <option value="frontend">Frontend</option>
              <option value="worker">Worker</option>
            </Select>
          </div>

          {/* Severity Filter */}
          <div className="md:col-span-2">
            <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} sizing="md">
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
            </Select>
          </div>

          {/* Date Range Filter */}
          <div className="md:col-span-2">
            <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)} sizing="md">
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
            </Select>
          </div>

          {/* User ID Filter */}
          <div className="md:col-span-3">
            <TextInput
              placeholder="Filter by user ID..."
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              sizing="md"
            />
          </div>
        </div>

        {/* Active Filters Display */}
        {(filters.source || severityFilter !== 'all' || dateRange !== 'all' || searchQuery || filters.userId) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Active filters:</span>
            {searchQuery && (
              <Badge color="info" size="sm">
                Search: {searchQuery}
                <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-red-500">
                  ×
                </button>
              </Badge>
            )}
            {filters.source && (
              <Badge color="purple" size="sm">
                Source: {filters.source}
                <button
                  onClick={() => handleFilterChange('source', '')}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )}
            {severityFilter !== 'all' && (
              <Badge color="warning" size="sm">
                Severity: {severityFilter}
                <button onClick={() => setSeverityFilter('all')} className="ml-1 hover:text-red-500">
                  ×
                </button>
              </Badge>
            )}
            {dateRange !== 'all' && (
              <Badge color="success" size="sm">
                Time: {dateRange}
                <button onClick={() => setDateRange('all')} className="ml-1 hover:text-red-500">
                  ×
                </button>
              </Badge>
            )}
            {filters.userId && (
              <Badge color="gray" size="sm">
                User: {filters.userId}
                <button
                  onClick={() => handleFilterChange('userId', '')}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )}
            <Button
              color="light"
              size="xs"
              onClick={() => {
                setSearchQuery('');
                setSeverityFilter('all');
                setDateRange('all');
                handleFilterChange('source', '');
                handleFilterChange('userId', '');
              }}
            >
              Clear all
            </Button>
          </div>
        )}
      </Card>

      {/* Errors List */}
      {loading ? (
        <div className="flex items-center justify-center rounded-lg bg-white py-12 shadow dark:bg-gray-800">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-sm text-gray-500">Loading errors...</p>
          </div>
        </div>
      ) : filteredErrors.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg bg-white p-12 shadow dark:bg-gray-800">
          <CheckCircleIcon className="h-16 w-16 text-green-500" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No errors found
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {errors.length > 0
              ? 'Try adjusting your filters'
              : 'All systems are running smoothly'}
          </p>
          {errors.length > 0 && (
            <Button
              color="light"
              size="sm"
              className="mt-4"
              onClick={() => {
                setSearchQuery('');
                setSeverityFilter('all');
                setDateRange('all');
                handleFilterChange('source', '');
                handleFilterChange('userId', '');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredErrors.map((error) => (
            <ErrorCard
              key={error.id}
              error={error}
              onMarkResolved={handleMarkResolved}
            />
          ))}
        </div>
      )}

      {/* Flowbite Pagination */}
      {pagination && pagination.total > pagination.limit && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              color="light"
              size="sm"
              onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
              disabled={filters.page === 1}
            >
              Previous
            </Button>
            <Button
              color="light"
              size="sm"
              onClick={() => handleFilterChange('page', filters.page + 1)}
              disabled={filters.page >= totalPages}
            >
              Next
            </Button>
          </div>

          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing{' '}
                <span className="font-medium">{(filters.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(filters.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>

            <Pagination
              currentPage={filters.page}
              totalPages={totalPages}
              onPageChange={(page) => handleFilterChange('page', page)}
              showIcons
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorCard({ error, onMarkResolved }) {
  const [expanded, setExpanded] = useState(false);

  // Get severity badge color
  const getSeverityBadge = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return { color: 'failure', icon: ExclamationTriangleIcon };
      case 'error':
        return { color: 'warning', icon: ExclamationCircleIcon };
      case 'warning':
        return { color: 'info', icon: InformationCircleIcon };
      default:
        return { color: 'gray', icon: InformationCircleIcon };
    }
  };

  const severityBadge = getSeverityBadge(error.severity);

  const handleCopyStackTrace = () => {
    if (error.stack_trace) {
      navigator.clipboard.writeText(error.stack_trace);
      toast.success('Stack trace copied to clipboard');
    }
  };

  const handleCopyError = () => {
    const errorText = `
Error ID: ${error.id}
Severity: ${error.severity}
Source: ${error.source}
Time: ${new Date(error.created_at).toLocaleString()}
User: ${error.user_id || 'N/A'}
Message: ${error.error_message}
${error.stack_trace ? `\nStack Trace:\n${error.stack_trace}` : ''}
${error.metadata ? `\nMetadata:\n${JSON.stringify(error.metadata, null, 2)}` : ''}
    `.trim();

    navigator.clipboard.writeText(errorText);
    toast.success('Error details copied to clipboard');
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Header with Badges */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge color={error.source === 'frontend' ? 'info' : 'purple'} size="sm">
              {error.source}
            </Badge>

            {error.severity && (
              <Badge color={severityBadge.color} icon={severityBadge.icon} size="sm">
                {error.severity}
              </Badge>
            )}

            <span className="text-xs text-gray-500">
              {new Date(error.created_at).toLocaleString()}
            </span>

            {error.user_id && (
              <Badge color="gray" size="sm">
                User: {error.user_id.substring(0, 12)}...
              </Badge>
            )}
          </div>

          {/* Error Message */}
          <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
            {error.error_message}
          </h3>

          {/* Expanded Details */}
          {expanded && (
            <div className="mt-4 space-y-4">
              {/* Stack Trace */}
              {error.stack_trace && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stack Trace:
                    </h4>
                    <Button color="light" size="xs" onClick={handleCopyStackTrace}>
                      <ClipboardDocumentIcon className="mr-1 h-3 w-3" />
                      Copy
                    </Button>
                  </div>
                  <pre className="max-h-64 overflow-auto rounded-lg bg-gray-900 p-3 text-xs text-gray-100 font-mono">
                    {error.stack_trace}
                  </pre>
                </div>
              )}

              {/* Metadata */}
              {error.metadata && Object.keys(error.metadata).length > 0 && (
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Metadata:
                  </h4>
                  <pre className="max-h-48 overflow-auto rounded-lg bg-gray-50 p-3 text-xs font-mono dark:bg-gray-700 dark:text-gray-200">
                    {JSON.stringify(error.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-700">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Error ID:</span>
                  <p className="font-mono text-xs text-gray-600 dark:text-gray-400">{error.id}</p>
                </div>
                {error.user_id && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">User ID:</span>
                    <p className="font-mono text-xs text-gray-600 dark:text-gray-400">
                      {error.user_id}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="ml-4 flex flex-col gap-2">
          <Button color="light" size="xs" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Hide' : 'Details'}
          </Button>

          <Button color="success" size="xs" onClick={() => onMarkResolved(error.id)}>
            <CheckCircleIcon className="mr-1 h-3 w-3" />
            Resolve
          </Button>

          <Button color="gray" size="xs" onClick={handleCopyError}>
            <ClipboardDocumentIcon className="mr-1 h-3 w-3" />
            Copy
          </Button>
        </div>
      </div>
    </Card>
  );
}
